/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import { Switch, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { font } from 'src/components/css'
import {
  TEAM_CONSTRAINT_CODES,
  getConstraintDisplayName,
  formatConstraintParameterSummary,
} from '../../constants'
import type { DivisionItem } from '../../hooks/useSchedulerDivisions'
import type {
  SchedulerConstraint,
  CreateConstraintData,
} from '../../hooks/useSchedulerConstraints'
import type { Venue } from '../../hooks/useSchedulerLocations'
import InlineConstraintForm from './InlineConstraintForm'

interface DivisionTeamsTableProps {
  division: DivisionItem
  constraints?: SchedulerConstraint[]
  onAddTeam?: (divisionId: string, teamName: string) => Promise<void>
  onDeleteTeam?: (divisionId: string, teamId: string, teamName: string) => void
  onUpdateTeamNotes?: (
    divisionId: string,
    teamId: string,
    notes: string
  ) => Promise<void>
  onAddConstraint?: (data: CreateConstraintData) => Promise<unknown>
  onDeleteConstraint?: (constraintId: string) => Promise<void>
  onToggleConstraintEnabled?: (constraintId: string, enabled: boolean) => void
  onUpdateConstraintType?: (constraintId: string, isHard: boolean) => void
  divisionId?: string
  venues?: Venue[]
}

const DivisionTeamsTable: React.FC<DivisionTeamsTableProps> = ({
  division,
  constraints = [],
  onDeleteTeam,
  onUpdateTeamNotes,
  onAddConstraint,
  onDeleteConstraint,
  onToggleConstraintEnabled,
  onUpdateConstraintType,
  divisionId,
  venues,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(new Set())
  const [addingForTeamId, setAddingForTeamId] = useState<string | null>(null)

  const toggleExpanded = (teamId: string) => {
    setExpandedRowKeys((prev) => {
      const next = new Set(prev)
      if (next.has(teamId)) {
        next.delete(teamId)
      } else {
        next.add(teamId)
      }
      return next
    })
  }

  const getTeamConstraints = (teamId: string) => {
    return constraints.filter(
      (c) =>
        // Constraint is explicitly scoped to this team (any type)
        (c.team_uids && c.team_uids.includes(teamId)) ||
        // OR constraint applies to all teams but is a team-tier type
        (c.team_uids === null &&
          TEAM_CONSTRAINT_CODES.includes(
            c.type.code as (typeof TEAM_CONSTRAINT_CODES)[number]
          ))
    )
  }

  // Auto-expand teams that already have constraints on initial load
  const hasAutoExpandedRef = useRef(false)
  useEffect(() => {
    if (hasAutoExpandedRef.current || constraints.length === 0) {
      return
    }
    hasAutoExpandedRef.current = true
    const ids = new Set<string>()
    for (const team of division.teams) {
      const hasConstraints = constraints.some(
        (c) =>
          (c.team_uids && c.team_uids.includes(team.id)) ||
          (c.team_uids === null &&
            TEAM_CONSTRAINT_CODES.includes(
              c.type.code as (typeof TEAM_CONSTRAINT_CODES)[number]
            ))
      )
      if (hasConstraints) {
        ids.add(team.id)
      }
    }
    if (ids.size > 0) {
      setExpandedRowKeys(ids)
    }
  }, [constraints, division.teams])

  return (
    <div css={sectionContainer}>
      {/* Section header */}
      <div css={sectionHeader}>
        <div css={sectionTitleGroup}>
          <span css={sectionTitle}>TEAMS</span>
          <span css={sectionSubtitle}>
            Add or remove teams and configure individual scheduling rules
          </span>
        </div>
      </div>

      {/* Table */}
      <div css={tableWrapper}>
        {/* Table header */}
        <div css={tableHeaderRow}>
          <div
            css={[headerCell, headerCellPadX, headerCellTeamName, teamNameCol]}
          >
            TEAM NAME
          </div>
          <div css={[headerCell, gamesCol]}>GAMES</div>
          <div css={[headerCell, playersCol]}>PLAYERS</div>
          <div css={[headerCell, notesCol]}>NOTES</div>
          <div css={[headerCell, headerCellPadX, actionsCol, actionsHidden]}>
            Actions
          </div>
        </div>

        {/* Table body */}
        {division.teams.length === 0 ? (
          <div css={emptyState}>No teams in this division</div>
        ) : (
          division.teams.map((team) => {
            const isExpanded = expandedRowKeys.has(team.id)
            const teamConstraints = getTeamConstraints(team.id)
            return (
              <React.Fragment key={team.id}>
                {/* Team row */}
                <div css={tableRow} onClick={() => toggleExpanded(team.id)}>
                  <div css={[bodyCell, teamNameCol, teamNameCellLayout]}>
                    <button
                      css={expandBtn}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleExpanded(team.id)
                      }}
                    >
                      <i
                        className={`fas fa-chevron-${
                          isExpanded ? 'up' : 'down'
                        }`}
                        css={expandChevron}
                      />
                    </button>
                    <div css={teamInfo}>
                      {team.image ? (
                        <img src={team.image} css={teamAvatar} alt='' />
                      ) : (
                        <div css={teamAvatarPlaceholder}>
                          <i className='fas fa-shield-alt' css={avatarIcon} />
                        </div>
                      )}
                      <span css={teamNameText}>{team.name}</span>
                    </div>
                  </div>
                  <div css={[bodyCell, gamesCol]}>
                    <span css={cellText}>
                      {team.gamesCount > 0 ? team.gamesCount : '-'}
                    </span>
                  </div>
                  <div css={[bodyCell, playersCol]}>
                    <span css={cellText}>
                      {division.playersCount > 0
                        ? `${division.playersCount} players`
                        : '-'}
                    </span>
                  </div>
                  <div
                    css={[bodyCell, notesCol]}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <NotesField
                      teamId={team.id}
                      divisionId={division.id}
                      value={team.schedulerTeamNotes || ''}
                      onSave={onUpdateTeamNotes}
                    />
                  </div>
                  <div
                    css={[bodyCell, actionsCol]}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div css={actionIcons}>
                      <Tooltip title='Add Constraint'>
                        <button
                          css={iconBtn}
                          onClick={() => {
                            if (!expandedRowKeys.has(team.id)) {
                              toggleExpanded(team.id)
                            }
                            setAddingForTeamId(team.id)
                          }}
                        >
                          <i className='fas fa-plus' />
                        </button>
                      </Tooltip>
                      <Tooltip title='Edit Team'>
                        <button css={iconBtn}>
                          <i className='fas fa-pen' />
                        </button>
                      </Tooltip>
                      <Tooltip title='Delete Team'>
                        <button
                          css={iconBtnDanger}
                          onClick={() =>
                            onDeleteTeam?.(division.id, team.id, team.name)
                          }
                        >
                          <i className='fas fa-trash' />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Expanded constraints sub-table */}
                {isExpanded && (
                  <div css={expandedSection}>
                    {/* Constraints sub-header */}
                    <div css={constraintHeaderRow}>
                      <div css={[constraintHeaderCell, cNumCol]}>#</div>
                      <div css={[constraintHeaderCell, cNameCol]}>
                        CONSTRAINTS
                      </div>
                      <div css={[constraintHeaderCell, cTypeCol]}>TYPE</div>
                      <div css={[constraintHeaderCell, cCategoryCol]}>
                        CATEGORY
                      </div>
                      <div css={[constraintHeaderCell, cActiveCol]}>ACTIVE</div>
                      <div
                        css={[constraintHeaderCell, cActionsCol, actionsHidden]}
                      >
                        Actions
                      </div>
                    </div>

                    {/* Inline add constraint form */}
                    {addingForTeamId === team.id && onAddConstraint && (
                      <InlineConstraintForm
                        teamId={team.id}
                        divisionId={divisionId || division.id}
                        onSave={async (data) => {
                          await onAddConstraint(data)
                          setAddingForTeamId(null)
                        }}
                        onCancel={() => setAddingForTeamId(null)}
                        venues={venues}
                      />
                    )}

                    {/* Constraint rows */}
                    {teamConstraints.length === 0 &&
                    addingForTeamId !== team.id ? (
                      <div css={noConstraints}>
                        No constraints applied to this team
                      </div>
                    ) : (
                      teamConstraints.map((constraint, cIndex) => (
                        <div key={constraint.id} css={constraintRow}>
                          <div css={[constraintCell, cNumCol]}>
                            <span css={constraintCellText}>{cIndex + 1}</span>
                          </div>
                          <div css={[constraintCell, cNameCol, cNameColLayout]}>
                            <span css={constraintName}>
                              {getConstraintDisplayName(
                                constraint.type?.code || '',
                                constraint.type?.name || 'Unnamed'
                              )}
                            </span>
                            {(() => {
                              const paramSummary =
                                formatConstraintParameterSummary(
                                  constraint.type?.code || '',
                                  constraint.parameters as Record<
                                    string,
                                    unknown
                                  >
                                )
                              const subtitle =
                                paramSummary || constraint.description
                              return subtitle ? (
                                <span css={constraintDesc}>{subtitle}</span>
                              ) : null
                            })()}
                          </div>
                          <div css={[constraintCell, cTypeCol]}>
                            <div css={typeBadgeGroup}>
                              <span
                                css={
                                  constraint.is_hard
                                    ? hardBadgeActive
                                    : hardBadgeInactive
                                }
                                onClick={() =>
                                  onUpdateConstraintType?.(constraint.id, true)
                                }
                              >
                                HARD
                              </span>
                              <span
                                css={
                                  constraint.is_hard
                                    ? softBadgeInactive
                                    : softBadgeActive
                                }
                                onClick={() =>
                                  onUpdateConstraintType?.(constraint.id, false)
                                }
                              >
                                SOFT
                              </span>
                            </div>
                          </div>
                          <div css={[constraintCell, cCategoryCol]}>
                            <span css={categoryBadge}>
                              {(constraint.type?.category || '-').toUpperCase()}
                            </span>
                          </div>
                          <div css={[constraintCell, cActiveCol]}>
                            <Switch
                              size='small'
                              checked={constraint.is_enabled}
                              onChange={(checked) =>
                                onToggleConstraintEnabled?.(
                                  constraint.id,
                                  checked
                                )
                              }
                              css={switchStyles}
                            />
                          </div>
                          <div css={[constraintCell, cActionsCol]}>
                            <div css={actionIcons}>
                              <Tooltip title='Clone Constraint'>
                                <button css={iconBtn}>
                                  <i className='fas fa-plus' />
                                </button>
                              </Tooltip>
                              <Tooltip title='Edit Constraint'>
                                <button css={iconBtn}>
                                  <i className='fas fa-pen' />
                                </button>
                              </Tooltip>
                              <Tooltip title='Delete Constraint'>
                                <button
                                  css={iconBtnDanger}
                                  onClick={() =>
                                    onDeleteConstraint?.(constraint.id)
                                  }
                                >
                                  <i className='fas fa-trash' />
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </React.Fragment>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─── Section header ──────────────────────────────────
const sectionContainer = css`
  margin-top: 24px;
`

const sectionHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const sectionTitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  color: ${colors.WHITE};
  text-transform: uppercase;
`

const sectionTitleGroup = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const sectionSubtitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
  font-weight: 400;
  line-height: 17.4px;
`

// ─── Table shell ─────────────────────────────────────
const tableWrapper = css`
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

// ─── Column widths (from Figma) ──────────────────────
const teamNameCol = css`
  width: 280px;
  min-width: 180px;
  flex-shrink: 0;
`
const gamesCol = css`
  width: 88px;
  flex-shrink: 0;
`
const playersCol = css`
  width: 88px;
  flex-shrink: 0;
`
const notesCol = css`
  width: 200px;
  min-width: 120px;
  flex: 1;
  position: relative;
  overflow: visible;
`
const actionsCol = css`
  width: 120px;
  flex-shrink: 0;
  padding-right: 16px;
  margin-left: auto;
  justify-content: flex-end;
`

// ─── Table header ────────────────────────────────────
const tableHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #212935;
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const headerCell = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  white-space: nowrap;
  padding: 8px 0;
`

const headerCellPadX = css`
  padding-left: 16px;
  padding-right: 16px;
`

const headerCellTeamName = css`
  letter-spacing: 1.44px;
`

const actionsHidden = css`
  opacity: 0;
`

// ─── Table body rows ─────────────────────────────────
const tableRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${colors.SECONDARY_400};
  cursor: pointer;

  &:hover {
    background-color: #212935;
  }
`

const bodyCell = css`
  padding: 8px;
  display: flex;
  align-items: center;
`

const teamNameCellLayout = css`
  gap: 4px;
`

const cellText = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
`

// ─── Team name cell ──────────────────────────────────
const expandBtn = css`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

const expandChevron = css`
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
`

const teamInfo = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const teamAvatar = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

const teamAvatarPlaceholder = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const avatarIcon = css`
  font-size: 16px;
  color: ${colors.WHITE};
`

const teamNameText = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// ─── Action icons ────────────────────────────────────
const actionIcons = css`
  display: flex;
  gap: 8px;
  align-items: center;
`

const iconBtn = css`
  background: transparent;
  border: none;
  color: ${colors.PRIMARY_300};
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover {
    opacity: 0.8;
  }
`

const iconBtnDanger = css`
  ${iconBtn};
  color: ${colors.ERROR_300};

  &:hover {
    opacity: 0.7;
  }
`

// ─── Expanded constraints ────────────────────────────
const expandedSection = css`
  background-color: ${colors.SECONDARY_500};
  overflow: hidden;
`

// Constraint column widths (from Figma)
const cNumCol = css`
  flex-shrink: 0;
  justify-content: center;
  padding-left: 16px;
  padding-right: 16px;
`
const cNameCol = css`
  flex: 1;
  min-width: 0;
`
const cNameColLayout = css`
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`
const cTypeCol = css`
  width: 112px;
  flex-shrink: 0;
  justify-content: center;
`
const cCategoryCol = css`
  width: 112px;
  flex-shrink: 0;
  justify-content: center;
`
const cActiveCol = css`
  width: 112px;
  flex-shrink: 0;
  justify-content: center;
`
const cActionsCol = css`
  width: 120px;
  flex-shrink: 0;
  padding-right: 16px;
  margin-left: auto;
  justify-content: flex-end;
`

const constraintHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 36px;
  border-bottom: 1px solid ${colors.SECONDARY_300};
`

const constraintHeaderCell = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.6px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  white-space: nowrap;
  padding: 8px 0;
  display: flex;
  align-items: center;
`

const constraintRow = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 36px;
  border-bottom: 0.8px solid #323a48;

  &:last-of-type {
    border-bottom: none;
  }
`

const constraintCell = css`
  padding: 8px;
  display: flex;
  align-items: center;
`

const constraintCellText = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};
`

const constraintName = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.WHITE};
`

const constraintDesc = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_100};
  display: block;
`

// ─── Type dual badge ─────────────────────────────────
const typeBadgeGroup = css`
  display: flex;
  border-radius: 4px;
  overflow: hidden;
`

const typeBadgeBase = css`
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.6px;
  line-height: 12px;
  text-transform: uppercase;
  padding: 4px 8px;
  white-space: nowrap;
  cursor: pointer;
`

const hardBadgeActive = css`
  ${typeBadgeBase};
  background-color: ${colors.ERROR_500};
  color: ${colors.WHITE};
`

const hardBadgeInactive = css`
  ${typeBadgeBase};
  background-color: ${colors.SECONDARY_400};
  color: ${colors.WHITE};
  opacity: 0.5;
`

const softBadgeActive = css`
  ${typeBadgeBase};
  background-color: ${colors.INFO_500};
  color: ${colors.NEUTRAL_30};
`

const softBadgeInactive = css`
  ${typeBadgeBase};
  background-color: ${colors.SECONDARY_400};
  color: ${colors.WHITE};
  opacity: 0.5;
`

// ─── Category badge ──────────────────────────────────
const categoryBadge = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.6px;
  line-height: 16px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
  background-color: #323a48;
  border-radius: 4px;
  padding: 4px 8px;
  white-space: nowrap;
`

// ─── Switch override ─────────────────────────────────
const switchStyles = css`
  &.ant-switch-checked {
    background-color: ${colors.PRIMARY_300} !important;
  }
`

// ─── Empty / no-constraints ──────────────────────────
const noConstraints = css`
  padding: 12px 36px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
`

const emptyState = css`
  padding: 24px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
`

// ─── Notes field component ───────────────────────────

interface NotesFieldProps {
  teamId: string
  divisionId: string
  value: string
  onSave?: (divisionId: string, teamId: string, notes: string) => Promise<void>
}

const NotesField: React.FC<NotesFieldProps> = ({
  teamId,
  divisionId,
  value,
  onSave,
}) => {
  const [draft, setDraft] = useState(value)
  const [focused, setFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [popoverPos, setPopoverPos] = useState<{
    top: number
    left: number
  } | null>(null)

  // Sync when value changes from outside (e.g. after refetch)
  useEffect(() => {
    if (!focused) {
      setDraft(value)
    }
  }, [value, focused])

  // Auto-resize textarea when focused and content changes
  useEffect(() => {
    const el = textareaRef.current
    if (el && focused) {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [draft, focused])

  const isDirty = draft.trim() !== value
  const hasContent = draft.trim().length > 0

  const handleOpen = () => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect()
      setPopoverPos({ top: rect.top, left: rect.left })
    }
    setFocused(true)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  const handleSave = () => {
    const trimmed = draft.trim()
    if (trimmed !== value) {
      onSave?.(divisionId, teamId, trimmed)
    }
    setFocused(false)
    textareaRef.current?.blur()
  }

  const handleClear = () => {
    setDraft('')
    if (value) {
      onSave?.(divisionId, teamId, '')
    }
    setFocused(false)
    textareaRef.current?.blur()
  }

  return (
    <>
      <div ref={anchorRef} css={notesFieldWrapper} onClick={handleOpen}>
        <span css={notesPreview}>
          {draft || <span css={notesPlaceholder}>Add note...</span>}
        </span>
      </div>
      {focused && (
        <div
          css={notesPopover}
          style={{ top: popoverPos?.top ?? 0, left: popoverPos?.left ?? 0 }}
        >
          <textarea
            ref={textareaRef}
            value={draft}
            placeholder='Add note...'
            rows={2}
            css={notesInput}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setTimeout(() => setFocused(false), 150)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setDraft(value)
                setFocused(false)
                textareaRef.current?.blur()
              }
            }}
          />
          <div css={notesActions}>
            {isDirty && (
              <Tooltip title='Save'>
                <button
                  type='button'
                  css={notesActionBtn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSave}
                >
                  <i className='fas fa-check' />
                </button>
              </Tooltip>
            )}
            {hasContent && (
              <Tooltip title='Clear'>
                <button
                  type='button'
                  css={notesActionBtnDanger}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClear}
                >
                  <i className='fas fa-times' />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </>
  )
}

const notesFieldWrapper = css`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
`

const notesPopover = css`
  position: fixed;
  width: 400px;
  z-index: 1000;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.PRIMARY_300};
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  padding: 4px;
  display: flex;
  align-items: flex-end;
  gap: 4px;
`

const notesInput = css`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 4px 6px;
  ${font.body}
  font-size: 13px;
  color: ${colors.NEUTRAL_50};
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;
  resize: none;
  line-height: 1.4;
  font-family: inherit;

  &::placeholder {
    color: ${colors.NEUTRAL_500};
  }

  &:hover {
    border-color: ${colors.SECONDARY_200};
  }

  &:focus {
    border-color: transparent;
    color: ${colors.NEUTRAL_0};
    background: transparent;
  }
`

const notesPreview = css`
  ${font.body}
  font-size: 13px;
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: text;
  padding: 4px 0;
  line-height: 1.4;
  width: 100%;
`

const notesPlaceholder = css`
  color: ${colors.NEUTRAL_500};
`

const notesActions = css`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`

const notesActionBtn = css`
  background: transparent;
  border: none;
  color: ${colors.SUCCESS_300};
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${font.body}
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background: rgba(16, 173, 50, 0.15);
  }
`

const notesActionBtnDanger = css`
  background: transparent;
  border: none;
  color: ${colors.ERROR_300};
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background: rgba(217, 27, 27, 0.15);
  }
`

export default DivisionTeamsTable
